import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useAppDispatch } from "hooks/redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useModal from "hooks/modals/useModal";

import {
  GetProductUnits,
  PostProductUnit,
  PutProductUnit,
  DeleteProductUnit,
} from "api/rpc/2024-04/facilityAdmin/product/unit";
import { StatusCode } from "api/protocols";

import { showError, showSuccess } from "redux/actions/ui";

import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import Input from "components/form/input";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import Popup from "components/popup/Popup";
import FormLayout from "components/form/FormLayout";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation, Trans } from "react-i18next";

import { TProductUnit } from "redux/reducers/models/product";

export default function ProductUnits() {
  const dispatch = useAppDispatch();

  const [units, setUnits] = useState<TProductUnit[]>(undefined);
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState<string>("");

  const {
    state: createUnitModal,
    updateModal: updateCreateUnitModal,
    closeModal: closeCreateUnitModal,
  } = useModal({
    name: "",
    abbreviation: "",
  });

  const {
    state: editUnitModal,
    updateModal: updateEditUnitModal,
    closeModal: closeEditUnitModal,
  } = useModal({
    id: null,
    name: "",
    abbreviation: "",
  });

  const {
    state: deleteUnitModal,
    updateModal: updateDeleteUnitModal,
    closeModal: closeDeleteUnitModal,
  } = useModal({
    id: null,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadUnits(source.token);

    return () => source.cancel();
  }, [search]);

  async function loadUnits(token?: CancelToken) {
    setUnits(undefined);

    const unitRes = await GetProductUnits({ search }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (unitRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.product_units.product_units.001")));
      return;
    }

    setUnits(unitRes.data);
  }

  async function createUnit() {
    if (createUnitModal.name === "") {
      dispatch(showError(t("secure.facility.settings.product_units.product_unit.002")));
      return;
    }

    const unitRes = await PostProductUnit(
      {
        name: createUnitModal.name,
        abbreviation: createUnitModal.abbreviation !== "" ? createUnitModal.abbreviation : undefined,
      },
      true,
    );

    if (unitRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.product_units.product_unit.003")));
      return;
    }

    void loadUnits();
    dispatch(showSuccess(t("secure.facility.settings.product_units.product_unit.004")));
    updateCreateUnitModal({ isOpen: false, name: "", abbreviation: "" });
  }

  async function updateUnit() {
    if (editUnitModal.id === null) {
      dispatch(showError(t("secure.facility.settings.product_units.product_unit.005")));
      return;
    }

    if (editUnitModal.name === "") {
      dispatch(showError(t("secure.facility.settings.product_units.product_unit.006")));
      return;
    }

    const unitRes = await PutProductUnit(
      {
        id: editUnitModal.id,
        name: editUnitModal.name,
        abbreviation: editUnitModal.abbreviation !== "" ? editUnitModal.abbreviation : undefined,
      },
      true,
    );

    if (unitRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.product_units.product_unit.007")));
      return;
    }

    void loadUnits();
    dispatch(showSuccess(t("secure.facility.settings.product_units.product_unit.008")));
    updateEditUnitModal({ isOpen: false, id: null, name: "", abbreviation: "" });
  }

  async function deleteUnit() {
    if (deleteUnitModal.id === null) {
      dispatch(showError(t("secure.facility.settings.product_units.product_unit.009")));
      return;
    }

    const unitRes = await DeleteProductUnit({ id: deleteUnitModal.id }, true);

    if (unitRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.product_units.product_unit.010")));
      return;
    }

    void loadUnits();
    dispatch(showSuccess(t("secure.facility.settings.product_units.product_unit.011")));
  }

  const primaryAction = {
    content: t("secure.facility.settings.product_units.product_unit.012"),
    action: () => updateCreateUnitModal({ isOpen: true }),
  };

  return (
    <Page title={t("secure.facility.settings.product_units.product_unit.013")} primaryAction={primaryAction} narrow>
      {" "}
      {/* TODO: Translation */}
      <div className="mb-4">
        <Input
          value={search}
          labelHidden
          placeholder={t("secure.facility.settings.product_units.product_unit.014")}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <DataTable
        columns={[
          { label: t("secure.facility.settings.product_units.product_unit.020") },
          { label: t("secure.facility.settings.product_units.product_unit.015") },
          { label: "", width: "10%" },
        ]}
        loading={units === undefined}
      >
        {units?.map(unit => (
          <tr key={unit.id}>
            <td>{unit.name}</td>
            <td>{unit.abbreviation}</td>
            <td>
              <div className="flex gap-2">
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id={`edit-product-unit`}>Edit {/* TODO: Translation */}</Tooltip>}
                >
                  <FontAwesomeIcon
                    icon={["far", "pencil"]}
                    onClick={() => updateEditUnitModal({ isOpen: true, ...unit })}
                  />
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id={`delete-product-unit`}>Delete {/* TODO: Translation */}</Tooltip>}
                >
                  <FontAwesomeIcon
                    icon={["far", "trash"]}
                    onClick={() => updateDeleteUnitModal({ isOpen: true, id: unit.id })}
                  />
                </OverlayTrigger>
              </div>
            </td>
          </tr>
        ))}
      </DataTable>
      <Portal isMounted={createUnitModal.isOpen}>
        <Sheet
          open={createUnitModal.isOpen}
          title={t("secure.facility.settings.product_units.product_unit.018")}
          size="small"
          onCancel={closeCreateUnitModal}
          okText={t("secure.facility.settings.product_units.product_unit.019")}
          onOk={createUnit}
        >
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={createUnitModal.name}
                label={t("secure.facility.settings.product_units.product_unit.020")}
                onChange={e => updateCreateUnitModal({ name: e.target.value })}
              />
              <Input
                value={createUnitModal.abbreviation}
                label={t("secure.facility.settings.product_units.product_unit.021")}
                onChange={e => updateCreateUnitModal({ abbreviation: e.target.value })}
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>
      <Portal isMounted={editUnitModal.isOpen}>
        <Sheet
          open={editUnitModal.isOpen}
          title={t("secure.facility.settings.product_units.product_unit.022")}
          size="small"
          onCancel={closeEditUnitModal}
          okText={t("secure.facility.settings.product_units.product_unit.023")}
          onOk={updateUnit}
        >
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={editUnitModal.name}
                label={t("secure.facility.settings.product_units.product_unit.024")}
                onChange={e => updateEditUnitModal({ name: e.target.value })}
              />
              <Input
                value={editUnitModal.abbreviation}
                label={t("secure.facility.settings.product_units.product_unit.025")}
                onChange={e => updateEditUnitModal({ abbreviation: e.target.value })}
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>
      <Portal isMounted={deleteUnitModal.isOpen}>
        <Popup
          open={deleteUnitModal.isOpen}
          type="warning"
          title={t("secure.facility.settings.product_units.product_unit.026")}
          description={t("secure.facility.settings.product_units.product_unit.027")}
          okText={t("secure.facility.settings.product_units.product_unit.028")}
          onOk={deleteUnit}
          onCancel={() => updateDeleteUnitModal({ isOpen: false, id: null })}
        />
      </Portal>
    </Page>
  );
}
